/**
 * One-click Practice Pulse enablement — reading the backend's answer (2026-09-25).
 *
 * teamio-admin 'Initialize Practice Pulse' runs three steps IN ORDER and answers for each:
 *   flag  — PRACTICE_PULSE_PAID_FOR = TRUE                          { ok, message }
 *   board — today's Firestore board built, or left alone if it
 *           already exists (unless force)                          { ok, status, opCount, message }
 *   exe   — Refresh Exe Settings; refused with a "use Restart App"
 *           message on exe < 3.0.56 (sent:false is a normal answer) { ok, sent, exeVersion, minExeVersion, message }
 * plus `message` (one-line summary) and `hqNote` (child clinics only, else null). An HTTP error means
 * the flag step itself failed.
 *
 * SuperAdmin shows every step's message VERBATIM, in that order. For the exe step "ok" means the
 * refresh was actually SENT: the step can run fine and still not reach the exe, and that is exactly
 * when the operator has something left to do (Restart App).
 *
 * Import-free: cypress/node-tests/practice-pulse-init-superadmin.test.js executes this file verbatim.
 */

export const PRACTICE_PULSE_INIT_ACTION = 'Initialize Practice Pulse';

const STEP_ORDER = [
  { key: 'flag', label: 'Setting' },
  { key: 'board', label: 'Board' },
  { key: 'exe', label: 'Exe' },
];

function stepsOf(data) {
  const steps = data && typeof data === 'object' ? data.steps : null;
  return steps && typeof steps === 'object' ? steps : {};
}

/**
 * The three lines of the result panel, always flag → board → exe. Never throws: a missing answer
 * (or a missing step) is a warning line reading "No answer.".
 */
export function describeInitializeSteps(data) {
  const steps = stepsOf(data);
  return STEP_ORDER.map(({ key, label }) => {
    const step = steps[key];
    if (!step || typeof step !== 'object') return { key, ok: false, label, message: 'No answer.' };
    const ok = key === 'exe' ? step.sent === true : step.ok === true;
    const given = typeof step.message === 'string' ? step.message.trim() : '';
    const fallback = key === 'exe' ? (ok ? 'Sent.' : 'Not sent.') : (ok ? 'Done.' : 'Not done.');
    return { key, ok, label, message: given || fallback };
  });
}

/** Does the operator still have to get the exe to re-read its settings? Yes unless it was sent. */
export function initializeNeedsExeReminder(data) {
  const exe = stepsOf(data).exe;
  return !(exe && exe.sent === true);
}

/** Was PRACTICE_PULSE_PAID_FOR actually saved? Only then may the switch show ON. */
export function initializeFlagSaved(data) {
  const flag = stepsOf(data).flag;
  return !!(flag && flag.ok === true);
}
